import { useCallback } from 'react';
import {
  type NavigateFunction,
  type NavigateOptions,
  type NavigationType,
  type To,
  useNavigate,
} from 'react-router-dom';
import type { ShellRouteKindType } from '@featit/shared';

/**
 * I-1167 (`docs/concept-iphone-ux.md`, IU-15, *Every back slides as a pop*): the one way the web
 * says a push or a replace means "back". The phone's shell slides every route change the web
 * starts, and it learns the direction from `routeChanging { kind }`: the router's own POP is a pop
 * already, but a back that goes *forward* in history — a form's Cancel, a cold open's back row, ‹
 * Settings, "Back to …" — is a push to the router, and slid in from the right until it said
 * otherwise.
 *
 * Every back-meaning `navigate`, `<Link>` and `setSearchParams` carries `backNavigation()` (or
 * goes through `useNavigateBack`); `backNavigation.guard.test.ts` refuses one that does not.
 */

const BACK_TRANSITION = 'pop';

/** React Router keeps the history index in `history.state.idx`; `0` is the entry the app opened on. */
export function historyIndex(): number {
  const state = window.history.state as { idx?: unknown } | null;
  return typeof state?.idx === 'number' ? state.idx : 0;
}

interface IBackNavigationState {
  readonly transition?: unknown;
}

/**
 * The navigate options that mark this navigation as a back: `{ state }`, merged into whatever
 * state and options the caller already passes (`replace` among them). Spread into `navigate`'s or
 * `setSearchParams`'s options, or pass its `state` to a `<Link>`.
 */
export function backNavigation(options: NavigateOptions = {}): NavigateOptions & {
  state: Record<string, unknown>;
} {
  const own =
    options.state && typeof options.state === 'object'
      ? (options.state as Record<string, unknown>)
      : {};
  return { ...options, state: { ...own, transition: BACK_TRANSITION } };
}

/** Whether a location's state was written by `backNavigation()`. */
export function isBackNavigationState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as IBackNavigationState).transition === BACK_TRANSITION
  );
}

/**
 * The kind `routeChanging` carries: `pop` for the router's POP (a history back, a swipe, the
 * shell's own `goBack()`) and for a push or replace marked by `backNavigation()`; `replace` for any
 * other REPLACE — a redirect, which changes the screen in place and slides nowhere; `push` for
 * every other change.
 */
export function routeTransitionKindOf(
  navigationType: NavigationType | `${NavigationType}`,
  state: unknown,
): ShellRouteKindType {
  if (navigationType === 'POP' || isBackNavigationState(state)) return 'pop';
  return navigationType === 'REPLACE' ? 'replace' : 'push';
}

/**
 * I-1167: how long a `goBackInHistory()` waits for the router's POP it asked for. The POP follows
 * within a frame or two; past this, a POP is someone else's (WebKit's edge swipe), never the web's.
 */
const WEB_STARTED_POP_WINDOW_MS = 1000;
let webStartedPopAt: number | null = null;

/**
 * The web's own step back in history — the one way it goes back that way, so the phone's shell
 * learns the POP that follows was the web's and slides it (`routeChanging { startedByWeb }`). Every
 * other POP is WebKit's swipe or the shell's own `goBack()`, which the shell leaves alone or
 * already animates. With `navigate`, through the router; without, `history.back()`.
 */
export function goBackInHistory(navigate?: NavigateFunction): void {
  webStartedPopAt = performance.now();
  if (navigate) void navigate(-1);
  else window.history.back();
}

/**
 * F-0567 (NX-6): `steps` entries back at once, as `goBackInHistory` steps one — a decision beside
 * its Decisions list returns past the list, to where the list was opened from.
 */
export function goBackStepsInHistory(steps: number, navigate: NavigateFunction): void {
  webStartedPopAt = performance.now();
  void navigate(-steps);
}

/** Whether this POP is the one `goBackInHistory()` asked for. Read once, by the POP it marks. */
export function consumeWebStartedPop(): boolean {
  const at = webStartedPopAt;
  webStartedPopAt = null;
  return at !== null && performance.now() - at < WEB_STARTED_POP_WINDOW_MS;
}

/**
 * Whether the web started this change as a back: a POP its own `goBackInHistory()` asked for, or a
 * push or replace marked by `backNavigation()`. A POP *to* an entry a marked back once wrote is
 * not one — that is a swipe or the shell's pop landing there — so a POP reads only the request.
 */
export function startedByWebAsBack(
  navigationType: NavigationType | `${NavigationType}`,
  state: unknown,
): boolean {
  // Consumed by every change, so a request that never became a POP marks no later one.
  const requested = consumeWebStartedPop();
  return navigationType === 'POP' ? requested : isBackNavigationState(state);
}

/** `navigate(to, backNavigation(options))`, for a handler that only ever goes back. */
export function useNavigateBack(): (to: To, options?: NavigateOptions) => void {
  const navigate = useNavigate();
  return useCallback(
    (to: To, options?: NavigateOptions) => navigate(to, backNavigation(options)),
    [navigate],
  );
}
